/**
 * 来場者向け 強制表示告知モーダル
 * 発表会などの告知を、投票モーダルと同じフェス風スタイルで表示します
 */

import React from 'react';
import { Image, Modal, Pressable, ScrollView, Text, View } from '../../../shared/components/WebPrimitives';
import ExternalLinkButton from '../../../shared/components/ExternalLinkButton';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import type { VotingFestAnnouncement } from '../services/visitorVotingFestService';
import FestivalOrnamentStrip from './FestivalOrnamentStrip';

/** 告知モーダルのプロパティ */
type VotingFestAnnouncementModalProps = {
  visible: boolean;
  announcement: VotingFestAnnouncement;
  onClose: () => void;
};

/**
 * 来場者向け 強制表示告知モーダル
 * @param {Object} props - プロパティ
 * @param {boolean} props.visible - 表示可否
 * @param {VotingFestAnnouncement} props.announcement - 表示する告知
 * @param {() => void} props.onClose - 閉じるコールバック
 * @returns {JSX.Element} 告知モーダル
 */
const VotingFestAnnouncementModal = ({
  visible,
  announcement,
  onClose,
}: VotingFestAnnouncementModalProps) => {
  /** 公開アプリ設定 */
  const { settings } = usePublicAppSettings();

  return (
    <Modal
      accessibilityLabel={`お知らせ: ${announcement.title}`}
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
      transparent
      visible={Boolean(visible && announcement.isEnabled)}
    >
      <View className="flex-1 items-center justify-center bg-black/90 px-2 py-3">
        {/* メインパネル */}
        <View
          className="flex max-h-[calc(100dvh-24px)] min-h-0 w-full max-w-[480px] flex-col overflow-hidden border-4 border-[#1A2D4D] shadow-[0_0_22px_rgba(255,215,0,0.85)]"
          testID="voting-announcement-dialog-panel"
        >
          <FestivalOrnamentStrip />

          {/* フェスヘッダー */}
          <View className="shrink-0 border-b-[3px] border-[#1A2D4D] bg-[#130B2E] px-3 pb-2 pt-[10px]">
            <View className="flex-row items-center gap-[6px]">
              <View className="h-px flex-1 bg-[#FFD700] opacity-[0.45]" />
              <Text className="text-[10px] font-black tracking-[2.5px] text-[#FFD700]">
                🎪 {settings.festivalLabelEn} 🎪
              </Text>
              <View className="h-px flex-1 bg-[#FFD700] opacity-[0.45]" />
            </View>

            <View className="mt-2 border-2 border-[#1A2D4D] bg-[#FFD700] px-[10px] py-[7px]">
              <Text
                className="text-center text-[15px] font-black tracking-[0.3px] text-[#130B2E]"
                role="heading"
              >
                📣 {announcement.title} 📣
              </Text>
            </View>
          </View>

          {/* コンテンツエリア */}
          <ScrollView
            className="min-h-0 flex-1 bg-[#FFFCE8]"
            contentContainerClassName="gap-2 p-[10px]"
            testID="voting-announcement-dialog-content"
          >
            {announcement.imageUrl ? (
              <View className="items-center border-2 border-[#1A2D4D] bg-[#FFF8DD] p-1">
                <Image
                  accessibilityLabel={`${announcement.title}の画像`}
                  className="h-auto max-h-[45dvh] w-full"
                  resizeMode="contain"
                  source={{ uri: announcement.imageUrl }}
                />
              </View>
            ) : null}

            {announcement.body ? (
              <View className="border-2 border-dashed border-[#1A2D4D] bg-[#FFF4C2] px-3 py-2">
                <Text className="whitespace-pre-wrap text-[13px] font-bold leading-relaxed text-[#1A2D4D]">
                  {announcement.body}
                </Text>
              </View>
            ) : null}
          </ScrollView>

          {/* 固定操作部 */}
          <View
            className="shrink-0 gap-2 border-t-2 border-[#1A2D4D] bg-[#FFFCE8] px-[10px] py-2"
            testID="voting-announcement-dialog-actions"
          >
            {announcement.linkUrl ? (
              <ExternalLinkButton
                className="min-h-[48px] border-[3px] border-[#1A2D4D] bg-[#FFD700] p-2 active:bg-[#E6C200]"
                href={announcement.linkUrl}
                label={`🔗 ${announcement.linkLabel}`}
                labelClassName="text-[15px] font-black tracking-[0.5px] text-[#130B2E]"
                testID="voting-announcement-link-button"
              />
            ) : null}

            <Pressable
              accessibilityRole="button"
              className="min-h-[48px] items-center justify-center border-[3px] border-[#FFD700] bg-[#1A2D4D] p-2 active:bg-[#0F1A2E]"
              onPress={onClose}
              testID="voting-announcement-close-button"
            >
              <Text className="text-[15px] font-black tracking-[0.5px] text-[#FFD700]">
                ✦ 閉じる ✦
              </Text>
            </Pressable>
          </View>

          <FestivalOrnamentStrip />
        </View>
      </View>
    </Modal>
  );
};

export default VotingFestAnnouncementModal;
